/* eslint-disable react-hooks/exhaustive-deps */
import { useDetermineLocale, useSDMContextProps } from '@/hooks';
import { SDMContexts } from '@ldp/sdm-contexts';
import { useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import GlobalState from '@/components/global-state';
import { cleanUp, isPharmaprixBanner, refreshTokenFlow, saveTokens, setLastVisitedPage } from '@/utils';
import * as Sentry from '@sentry/react';
import messages, { type Locale } from 'I18n/translations';
import { usePathname } from 'next/navigation';
import { Spinner } from 'Shared/spinner';
import { LayoutProps } from './layout.interface';
import { QueryClientSideProvider } from './QueryClientProviders';

export default function Layout({ children, cartData, viewDefinitionResults, kamFlags }: LayoutProps) {
  const locale = useDetermineLocale();
  const [preferredLocale, setPreferredLocale] = useState<Locale>('en');
  const sdmContextProps = useSDMContextProps();
  const pathname = usePathname();
  const [refreshAvailable, setRefreshAvailable] = useState(false);
  const [isClient, setIsClient] = useState(false);

  const refreshFlow = async () => {
    try {
      const tokens = await refreshTokenFlow();
      if (tokens !== null) {
        Sentry.captureMessage('Refresh Token successfully');
        console.log('saving tokens', tokens);
        saveTokens(tokens);
        const isUnauthorized = Number(viewDefinitionResults?.error?.status) === 401;
        setRefreshAvailable(!isUnauthorized);
        if (isUnauthorized) {
          window.location.href = pathname;
        }
      } else {
        setRefreshAvailable(true);
      }
    } catch (error) {
      Sentry.addBreadcrumb({ message: 'Refresh flow failed on Layout' });
      Sentry.captureException(error);
    }
  };

  useEffect(() => setPreferredLocale(locale), [locale]);
  useEffect(() => setLastVisitedPage(pathname), [pathname]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (isClient) {
      const domains = isPharmaprixBanner()
        ? ['.pharmaprix.ca', process.env.NEXT_PUBLIC_COOKIE_DOMAIN_FR_URL]
        : ['.shoppersdrugmart.ca', process.env.NEXT_PUBLIC_COOKIE_DOMAIN_EN_URL];
      domains.forEach((domain) => cleanUp(domain));
      // Run refresh flow asynchronously without blocking render
      refreshFlow().catch(() => {
        setRefreshAvailable(true);
      });
    }
  }, [isClient]);

  const pageContent = (
    <GlobalState
      cartData={cartData ?? undefined}
      viewDefinitionResults={viewDefinitionResults ?? undefined}
      kamFlags={kamFlags ?? null}
    >
      <SDMContexts {...sdmContextProps}>
        <IntlProvider
          locale={preferredLocale}
          messages={messages[preferredLocale]}
          onError={() => {
            return 'no error';
          }}
        >
          <QueryClientSideProvider>{children}</QueryClientSideProvider>
        </IntlProvider>
      </SDMContexts>
    </GlobalState>
  );

  // Render content immediately instead of blocking - token refresh happens in background
  // This prevents blocking the initial page render and improves LCP
  if (!isClient) {
    return <noscript>{pageContent}</noscript>;
  }

  return refreshAvailable ? pageContent : <Spinner />;
}
